// @flow
/* eslint-disable no-restricted-globals, react-hooks/exhaustive-deps */

/**
 * This component depends on ta.stored JSON delivered to the page via either the "enable_cpm_content(_mobile)" placement or the CpmParamsResult component (or similar)
 * Sample config for use in a placement: renderer_params: component:@ta/cpm.ad-target-for-modal, size:300x250, modalServlet:locationphotoalbum
 *
 * For this component to work:
 *   1) There must be an ad defined (correct size) for the modal servlet in AdDecider.java
 *   2) The adStub JSON must be available on the page via ta.retrieve: ta.retrieve('ads.{modalServlet}.adStubs')
 */

import React, { useEffect } from 'react';
import type { ValidSize } from '@ta/cpm.ad-target';
import { buildSizeAttribute } from '@ta/cpm.ad-target';
import { registerRenderEventListenerWithWrapper } from '@ta/cpm.adsupport';
import WindowResize from '@ta/events.window-resize';
import Radio from '@ta/common.radio';
import styles from './ad-target-for-modal.less';

type Props = {|
  size: ValidSize, // 300x250 etc.
  position: string, // a, b, c, -> 300x250-a-{modalServlet}
  centered?: boolean, // ad-target will be centered to its parent, ignored by mobile, since mobile always centers
  additionalWrapperClass: string, // For any (minimal!) additional styling on the ad parent element
  minWinSize: string, // Optional screen size minimum below which the ad will be hidden/disabled
  modalServlet?: string, // The servlet for which the ad targets are defined in AdDecider.java
  defer: boolean, // Allow ad injection to be triggered externally via the radio
  radio?: Radio, // Used to refresh the ad
  hidden: boolean, // Use to hide the ad, only pixels can be injected when hidden
  onRender?: any => void, // Callback for listening to actual renders of an ad
  source?: string, // whether ad is for a modal(like "tripsModal") or a page (like "tripsDetail")
|};

const noop = () => {};

function AdTargetForModal(props: Props) {
  AdTargetForModal.defaultProps = {
    position: '',
    additionalWrapperClass: '',
    minWinSize: '0',
    defer: false,
    hidden: false,
    centered: false,
  };

  const {
    size,
    centered,
    position,
    additionalWrapperClass,
    minWinSize,
    modalServlet,
    defer,
    radio,
    hidden,
    onRender = noop,
    source = 'modal',
  } = props;

  function buildTargetClass() {
    return [
      'gptAd',
      source && source.toLowerCase().includes('modal') ? 'delayAd' : '',
      styles.gptAd,
      centered ? styles.centered : '',
      hidden ? 'hidden' : '',
    ].join(' ');
  }

  function buildAdId(targetSize: string, servlet: ?string, targetPosition: string) {
    const sizeLower = targetSize.toLowerCase();
    const servletLower = servlet ? `-${servlet}`.toLowerCase() : '';
    const posLower = targetPosition ? `-${targetPosition}`.toLowerCase() : '';
    return `gpt-ad-${sizeLower}${posLower}${servletLower}`;
  }

  // Use CustomEvent to notify AMD Ads JS
  function requestAdDisplay(id: string, servlet: ?string) {
    const adOpts = { elemIds: [id], modalServlet: servlet && servlet.toLowerCase() };
    let modalAdRequest;
    if (window.CustomEvent && typeof window.CustomEvent === 'function') {
      modalAdRequest = new CustomEvent('cpmModalDisplayRequest', {
        bubbles: true,
        cancelable: true,
        detail: adOpts,
      });
      // IE
    } else {
      modalAdRequest = document.createEvent('CustomEvent');
      modalAdRequest.initCustomEvent('cpmModalDisplayRequest', true, true, adOpts);
    }
    // Ensure DoubleClick API load (generally not a problem for modals)
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(() => {
      setTimeout(() => {
        document.dispatchEvent(modalAdRequest);
      }, 0);
    });
  }

  function disableTargetAtMinWinSize(reactRef) {
    if (minWinSize !== '0') {
      const screenWidth = window.innerWidth;
      const minWidth = parseInt(minWinSize, 10) || 0;
      let target;
      if (reactRef.current) {
        target = reactRef.current.querySelector('.gptAd');
      }
      if (target && target.classList) {
        const disabled = target.classList.contains('inactive');
        if (screenWidth < minWidth && !disabled) {
          target.classList.add('inactive');
          target.classList.add('hidden');
        } else if (screenWidth >= minWidth && disabled) {
          target.classList.remove('inactive');
          target.classList.remove('hidden');
        }
      }
    }
  }

  const targetClass = buildTargetClass();
  const targetSizeAttribute = buildSizeAttribute(size);
  const adId = buildAdId(size, modalServlet, position);

  const wrapperRef = React.useRef(null);
  const adRef = React.useRef(null);

  useEffect(() => {
    if (!defer) {
      requestAdDisplay(adId, modalServlet);
    }
    if (radio) {
      const refreshFn = () => requestAdDisplay(adId, modalServlet);
      radio.on('refreshAds', refreshFn);

      // prevent duplicate handlers on re-mount
      return () => radio.off('refreshAds', refreshFn);
    }
    return () => {};
  }, []);

  useEffect(() => {
    registerRenderEventListenerWithWrapper(adId, wrapperRef, onRender);
  }, []);

  return (
    <>
      {minWinSize !== '0' && (
        <WindowResize callback={() => disableTargetAtMinWinSize(wrapperRef)} throttle={300} />
      )}

      <div className={additionalWrapperClass} ref={wrapperRef}>
        <div id={adId} className={targetClass} data-size={targetSizeAttribute} ref={adRef} />
      </div>
    </>
  );
}

export default AdTargetForModal;
